import type { AddLayerObject, MapOptions } from "maplibre-gl";

export const KARTVERKET_TOPO =
  "https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png";

export const SEVERITY_COLORS = { high: "#c0392b", medium: "#e67e22", low: "#f1c40f" } as const;
export const COLORS = {
  route: "#2463a6",
  catchment: "#2463a6",
  crossingWith: "#1e8449",
  crossingWithout: "#ffffff",
  crossingStroke: "#34495e",
  accident: "#6c3483",
  accidentLight: "#a569bd",
  fau: "#0e7c86",
  school: "#14365c",
  halo: "#1d2733",
} as const;

type StyleSpec = Exclude<MapOptions["style"], string | undefined>;
type LineLayer = Extract<AddLayerObject, { type: "line" }>;
/** maplibre's expression type, derived from its public layer types. */
type Expression = Extract<NonNullable<NonNullable<LineLayer["paint"]>["line-color"]>, unknown[]>;
type Filter = NonNullable<LineLayer["filter"]>;

export const SOURCES = {
  catchment: "catchment",
  routes: "routes",
  findings: "findings",
  crossings: "crossings",
  accidents: "accidents",
  clusters: "accident-clusters",
  fau: "fau-reports",
  school: "school",
} as const;

export type LayerGroup = "catchment" | "routes" | "findings" | "crossings" | "accidents" | "fau";
export type LayerVisibility = Record<LayerGroup, boolean>;

export const DEFAULT_LAYERS: LayerVisibility = {
  catchment: true,
  routes: true,
  findings: true,
  crossings: true,
  accidents: false,
  fau: true,
};

export const LAYER_GROUPS: Record<LayerGroup, string[]> = {
  catchment: ["catchment-fill", "catchment-line"],
  routes: ["routes"],
  findings: ["findings-halo-line", "findings-halo-point", "findings-line", "findings-point"],
  crossings: ["crossings"],
  accidents: ["accident-clusters", "accidents"],
  fau: ["fau-reports"],
};

/** Layers that open the finding panel when clicked. */
export const CLICKABLE_FINDING_LAYERS = ["findings-line", "findings-point"];

export function baseStyle(): StyleSpec {
  return {
    version: 8,
    sources: {
      topo: {
        type: "raster",
        tiles: [KARTVERKET_TOPO],
        tileSize: 256,
        maxzoom: 18,
        attribution: "© Kartverket",
      },
    },
    layers: [
      { id: "background", type: "background", paint: { "background-color": "#eef1f4" } },
      { id: "topo", type: "raster", source: "topo" },
    ],
  };
}

const severityColor: Expression = [
  "match",
  ["get", "severity"],
  "high",
  SEVERITY_COLORS.high,
  "medium",
  SEVERITY_COLORS.medium,
  "low",
  SEVERITY_COLORS.low,
  "#7f8c8d",
];

const dimmedOpacity: Expression = [
  "case",
  ["in", ["get", "status"], ["literal", ["utfort", "ikke_relevant"]]],
  0.35,
  0.95,
];

const isLine: Expression = [
  "in",
  ["geometry-type"],
  ["literal", ["LineString", "MultiLineString"]],
];
const isPoint: Expression = ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]];

export function selectedFilter(key: string | null, geometry: "line" | "point"): Filter {
  return ["all", geometry === "line" ? isLine : isPoint, ["==", ["get", "key"], key ?? ""]];
}

export function routeWidth(maxExposure: number): Expression {
  return ["interpolate", ["linear"], ["get", "exposure"], 0, 1, Math.max(1, maxExposure), 10];
}

/** Overlay layers, bottom to top. */
export function overlayLayers(): AddLayerObject[] {
  return [
    {
      id: "catchment-fill",
      type: "fill",
      source: SOURCES.catchment,
      paint: { "fill-color": COLORS.catchment, "fill-opacity": 0.08 },
    },
    {
      id: "catchment-line",
      type: "line",
      source: SOURCES.catchment,
      paint: { "line-color": COLORS.catchment, "line-width": 1.5, "line-dasharray": [3, 2] },
    },
    {
      id: "routes",
      type: "line",
      source: SOURCES.routes,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": COLORS.route, "line-width": routeWidth(1), "line-opacity": 0.7 },
    },
    {
      id: "accident-clusters",
      type: "circle",
      source: SOURCES.clusters,
      layout: { visibility: "none" },
      paint: {
        "circle-color": COLORS.accident,
        "circle-opacity": 0.25,
        "circle-stroke-color": COLORS.accident,
        "circle-stroke-width": 2,
        "circle-radius": ["interpolate", ["linear"], ["get", "count"], 2, 12, 10, 24],
      },
    },
    {
      id: "accidents",
      type: "circle",
      source: SOURCES.accidents,
      layout: { visibility: "none" },
      paint: {
        "circle-color": [
          "case",
          ["==", ["get", "soft"], true],
          COLORS.accident,
          COLORS.accidentLight,
        ],
        "circle-radius": 4,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1,
      },
    },
    {
      id: "crossings",
      type: "circle",
      source: SOURCES.crossings,
      paint: {
        "circle-color": [
          "case",
          ["==", ["get", "has_facility"], true],
          COLORS.crossingWith,
          COLORS.crossingWithout,
        ],
        "circle-stroke-color": [
          "case",
          ["==", ["get", "has_facility"], true],
          "#ffffff",
          COLORS.crossingStroke,
        ],
        "circle-stroke-width": 1.5,
        "circle-radius": 4,
      },
    },
    {
      id: "findings-halo-line",
      type: "line",
      source: SOURCES.findings,
      filter: selectedFilter(null, "line"),
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": COLORS.halo, "line-width": 14, "line-opacity": 0.55 },
    },
    {
      id: "findings-halo-point",
      type: "circle",
      source: SOURCES.findings,
      filter: selectedFilter(null, "point"),
      paint: { "circle-color": COLORS.halo, "circle-radius": 15, "circle-opacity": 0.55 },
    },
    {
      id: "findings-line",
      type: "line",
      source: SOURCES.findings,
      filter: isLine,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": severityColor, "line-width": 6, "line-opacity": dimmedOpacity },
    },
    {
      id: "findings-point",
      type: "circle",
      source: SOURCES.findings,
      filter: isPoint,
      paint: {
        "circle-color": severityColor,
        "circle-opacity": dimmedOpacity,
        "circle-stroke-opacity": dimmedOpacity,
        "circle-radius": 8,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    },
    {
      id: "fau-reports",
      type: "circle",
      source: SOURCES.fau,
      paint: {
        "circle-color": COLORS.fau,
        "circle-radius": 6,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    },
    {
      id: "school",
      type: "circle",
      source: SOURCES.school,
      paint: {
        "circle-color": COLORS.school,
        "circle-radius": 9,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 3,
      },
    },
  ];
}
